import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { kycAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Loader2, CheckCircle, XCircle, Clock, Shield, 
  User, MapPin, CreditCard, FileText, Upload, AlertTriangle, File, Trash2
} from 'lucide-react';

const KYCVerification = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [kycStatus, setKycStatus] = useState(null);
  const [uploadedDocs, setUploadedDocs] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState({});
  const [step, setStep] = useState(1);
  
  // File input refs
  const idFrontRef = useRef(null);
  const idBackRef = useRef(null);
  const selfieRef = useRef(null);
  const addressProofRef = useRef(null);
  
  // Form data
  const [formData, setFormData] = useState({
    // Personal Info
    full_legal_name: '',
    date_of_birth: '',
    nationality: '',
    // Address
    address_line1: '',
    address_line2: '',
    city: '',
    state_province: '',
    postal_code: '',
    country: '',
    // ID Verification
    id_type: 'passport',
    id_number: '',
    id_expiry_date: '',
    id_front_url: '',
    id_back_url: '',
    selfie_url: '',
    // Address Proof
    address_proof_type: 'utility_bill',
    address_proof_url: '',
    // Bank Details
    bank_name: '',
    bank_account_name: '',
    bank_account_number: '',
    bank_routing_number: '',
    bank_swift_code: '',
    bank_iban: ''
  });

  useEffect(() => {
    fetchKYCData();
  }, []);

  const fetchKYCData = async () => {
    try {
      const [statusRes, docsRes] = await Promise.all([
        kycAPI.getStatus(),
        kycAPI.getDocuments().catch(() => ({ data: [] }))
      ]);
      setKycStatus(statusRes.data);
      
      // Convert docs array to object by document_type
      const docsMap = {};
      docsRes.data.forEach(doc => {
        docsMap[doc.document_type] = doc;
      });
      setUploadedDocs(docsMap);
    } catch (error) {
      console.error('Failed to fetch KYC data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (documentType, file) => {
    if (!file) return;
    
    // Validate file
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPG, PNG, and PDF files are allowed');
      return;
    }
    
    if (file.size > maxSize) {
      toast.error('File size must be less than 10MB');
      return;
    }
    
    setUploading(prev => ({ ...prev, [documentType]: true }));
    
    try {
      const response = await kycAPI.uploadDocument(documentType, file);
      
      setUploadedDocs(prev => ({
        ...prev,
        [documentType]: {
          document_type: documentType,
          original_filename: file.name,
          status: 'pending_review',
          file_id: response.data.file_id
        }
      }));
      
      // Update form data with placeholder URL (backend manages actual file)
      const urlField = documentType === 'id_front' ? 'id_front_url' :
                      documentType === 'id_back' ? 'id_back_url' :
                      documentType === 'selfie' ? 'selfie_url' :
                      'address_proof_url';
      
      setFormData(prev => ({
        ...prev,
        [urlField]: `uploaded:${response.data.file_id}`
      }));
      
      toast.success(`${documentType.replace('_', ' ')} uploaded successfully`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(prev => ({ ...prev, [documentType]: false }));
    }
  };

  const handleSubmit = async () => {
    // Validate required fields
    const requiredFields = [
      'full_legal_name', 'date_of_birth', 'nationality',
      'address_line1', 'city', 'state_province', 'postal_code', 'country',
      'id_type', 'id_number', 'id_expiry_date',
      'bank_name', 'bank_account_name', 'bank_account_number'
    ];
    
    const missing = requiredFields.filter(f => !formData[f]);
    if (missing.length > 0) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Check required documents
    const requiredDocs = ['id_front', 'selfie'];
    if (formData.id_type === 'drivers_license') {
      requiredDocs.push('id_back');
    }
    
    const missingDocs = requiredDocs.filter(doc => !uploadedDocs[doc]);
    if (missingDocs.length > 0) {
      toast.error(`Please upload: ${missingDocs.map(d => d.replace('_', ' ')).join(', ')}`);
      return;
    }
    
    setSubmitting(true);
    try {
      // Use placeholder URLs from uploaded docs
      const submitData = {
        ...formData,
        id_front_url: uploadedDocs.id_front?.file_id || formData.id_front_url,
        id_back_url: uploadedDocs.id_back?.file_id || formData.id_back_url,
        selfie_url: uploadedDocs.selfie?.file_id || formData.selfie_url,
        address_proof_url: uploadedDocs.address_proof?.file_id || formData.address_proof_url
      };
      
      await kycAPI.submit(submitData);
      toast.success('Verification documents submitted successfully!');
      await refreshUser();
      fetchKYCData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit verification');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified':
      case 'approved':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'rejected':
        return <XCircle className="w-4 h-4 text-destructive" />;
      case 'pending_review':
      case 'submitted':
        return <Clock className="w-4 h-4 text-yellow-400" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      verified: 'bg-primary/20 text-primary border-primary/30',
      approved: 'bg-green-500/20 text-green-400 border-green-500/30',
      rejected: 'bg-destructive/20 text-destructive border-destructive/30',
      submitted: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      under_review: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      pending_review: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      pending: 'bg-muted text-muted-foreground border-muted'
    };
    return colors[status] || colors.pending;
  };

  const FileUploadBox = ({ label, documentType, inputRef, accept = ".jpg,.jpeg,.png,.pdf" }) => {
    const doc = uploadedDocs[documentType];
    const isUploading = uploading[documentType];
    
    return (
      <div className="space-y-2">
        <Label className="text-sm">{label}</Label>
        <div 
          className={`p-4 rounded-lg border-2 border-dashed transition-colors cursor-pointer
            ${doc ? 'border-green-500/50 bg-green-500/5' : 'border-muted-foreground/30 hover:border-primary/50 bg-secondary/20'}`}
          onClick={() => !isUploading && inputRef.current?.click()}
        >
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            className="hidden"
            onChange={(e) => handleFileUpload(documentType, e.target.files[0])}
            disabled={isUploading}
          />
          
          {isUploading ? (
            <div className="flex items-center justify-center gap-2 py-2">
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">Uploading...</span>
            </div>
          ) : doc ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <File className="w-5 h-5 text-green-500" />
                <div>
                  <p className="text-sm font-medium truncate max-w-[200px]">{doc.original_filename}</p>
                  <div className="flex items-center gap-1 mt-0.5">
                    {getStatusIcon(doc.status)}
                    <span className="text-xs text-muted-foreground capitalize">
                      {doc.status?.replace('_', ' ')}
                    </span>
                  </div>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  inputRef.current?.click();
                }}
              >
                Replace
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-2">
              <Upload className="w-8 h-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">Click to upload</p>
              <p className="text-xs text-muted-foreground/70 mt-1">JPG, PNG or PDF (max 10MB)</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // If already verified
  if (kycStatus?.status === 'verified') {
    return (
      <div className="min-h-screen bg-background pt-20" data-testid="kyc-verified">
        <div className="max-w-2xl mx-auto px-4 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="bg-card border-primary/50">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-12 h-12 text-primary" />
                </div>
                <h2 className="font-heading text-2xl font-bold uppercase mb-2 text-primary">
                  Verification Complete
                </h2>
                <p className="text-muted-foreground mb-6">
                  Your identity has been verified. You can now request payouts from your funded accounts.
                </p>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="p-3 rounded bg-secondary/30 text-center">
                    <User className="w-5 h-5 text-primary mx-auto mb-1" />
                    <span className="text-xs text-muted-foreground">ID Verified</span>
                  </div>
                  <div className="p-3 rounded bg-secondary/30 text-center">
                    <MapPin className="w-5 h-5 text-primary mx-auto mb-1" />
                    <span className="text-xs text-muted-foreground">Address Verified</span>
                  </div>
                  <div className="p-3 rounded bg-secondary/30 text-center">
                    <CreditCard className="w-5 h-5 text-primary mx-auto mb-1" />
                    <span className="text-xs text-muted-foreground">Bank Verified</span>
                  </div>
                </div>
                <Button onClick={() => navigate('/dashboard')} className="font-heading uppercase">
                  Go to Dashboard
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    );
  }

  // If submitted/under review
  if (kycStatus?.status === 'submitted' || kycStatus?.status === 'under_review') {
    return (
      <div className="min-h-screen bg-background pt-20" data-testid="kyc-pending">
        <div className="max-w-2xl mx-auto px-4 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="bg-card border-yellow-500/50">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 rounded-full bg-yellow-500/10 flex items-center justify-center mx-auto mb-6">
                  <Clock className="w-12 h-12 text-yellow-400" />
                </div>
                <h2 className="font-heading text-2xl font-bold uppercase mb-2 text-yellow-400">
                  Verification Under Review
                </h2>
                <p className="text-muted-foreground mb-6">
                  Your documents are being reviewed. This typically takes 24-48 hours. 
                  We'll notify you once the verification is complete.
                </p>
                <Badge variant="outline" className={getStatusBadge(kycStatus.status)}>
                  {kycStatus.status.replace('_', ' ').toUpperCase()}
                </Badge>
                <div className="mt-6">
                  <Button variant="outline" onClick={() => navigate('/dashboard')}>
                    Go to Dashboard
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    );
  }

  // If rejected
  if (kycStatus?.status === 'rejected') {
    return (
      <div className="min-h-screen bg-background pt-20" data-testid="kyc-rejected">
        <div className="max-w-2xl mx-auto px-4 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="bg-card border-destructive/50">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-6">
                  <XCircle className="w-12 h-12 text-destructive" />
                </div>
                <h2 className="font-heading text-2xl font-bold uppercase mb-2 text-destructive">
                  Verification Rejected
                </h2>
                <p className="text-muted-foreground mb-4">
                  Unfortunately, your verification was not approved.
                </p>
                {kycStatus.rejection_reason && (
                  <div className="p-4 rounded bg-destructive/10 border border-destructive/30 mb-6 text-left">
                    <p className="text-sm font-medium text-destructive">Reason:</p>
                    <p className="text-sm text-muted-foreground">{kycStatus.rejection_reason}</p>
                  </div>
                )}
                <Button onClick={() => setStep(1)} className="font-heading uppercase">
                  Resubmit Documents
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    );
  }

  // Verification Form
  return (
    <div className="min-h-screen bg-background pt-20" data-testid="kyc-form">
      <div className="max-w-3xl mx-auto px-4 py-8 md:py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <Shield className="w-12 h-12 text-primary mx-auto mb-4" />
          <h1 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight mb-2">
            Identity Verification
          </h1>
          <p className="text-muted-foreground text-sm md:text-base">
            Complete verification to request payouts from your funded accounts. All documents are stored securely and handled according to GDPR guidelines.
          </p>
        </motion.div>

        {/* Progress Steps */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4].map((s) => (
              <React.Fragment key={s}>
                <div 
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold cursor-pointer transition-colors ${
                    step === s ? 'bg-primary text-primary-foreground' : 
                    step > s ? 'bg-primary/30 text-primary' : 'bg-muted text-muted-foreground'
                  }`}
                  onClick={() => setStep(s)}
                >
                  {s}
                </div>
                {s < 4 && <div className={`w-8 h-0.5 ${step > s ? 'bg-primary' : 'bg-muted'}`} />}
              </React.Fragment>
            ))}
          </div>
        </div>

        <Card className="bg-card border-border">
          <CardContent className="p-6">
            {/* Step 1: Personal Info */}
            {step === 1 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <div className="flex items-center gap-2 mb-6">
                  <User className="w-5 h-5 text-primary" />
                  <h3 className="font-heading text-lg uppercase">Personal Information</h3>
                </div>
                <div className="grid gap-4">
                  <div>
                    <Label>Full Legal Name (as on ID)</Label>
                    <Input
                      value={formData.full_legal_name}
                      onChange={(e) => handleInputChange('full_legal_name', e.target.value)}
                      placeholder="John Doe"
                      className="bg-input/50 mt-1"
                      data-testid="kyc-name"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Date of Birth</Label>
                      <Input
                        type="date"
                        value={formData.date_of_birth}
                        onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                        className="bg-input/50 mt-1"
                        data-testid="kyc-dob"
                      />
                    </div>
                    <div>
                      <Label>Nationality</Label>
                      <Input
                        value={formData.nationality}
                        onChange={(e) => handleInputChange('nationality', e.target.value)}
                        placeholder="British"
                        className="bg-input/50 mt-1"
                        data-testid="kyc-nationality"
                      />
                    </div>
                  </div>
                </div>
                <div className="flex justify-end mt-6">
                  <Button onClick={() => setStep(2)} className="font-heading uppercase">
                    Next: Address
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 2: Address */}
            {step === 2 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <div className="flex items-center gap-2 mb-6">
                  <MapPin className="w-5 h-5 text-primary" />
                  <h3 className="font-heading text-lg uppercase">Address Details</h3>
                </div>
                <div className="grid gap-4">
                  <div>
                    <Label>Address Line 1</Label>
                    <Input
                      value={formData.address_line1}
                      onChange={(e) => handleInputChange('address_line1', e.target.value)}
                      placeholder="123 Main Street"
                      className="bg-input/50 mt-1"
                      data-testid="kyc-address1"
                    />
                  </div>
                  <div>
                    <Label>Address Line 2 (Optional)</Label>
                    <Input
                      value={formData.address_line2}
                      onChange={(e) => handleInputChange('address_line2', e.target.value)}
                      placeholder="Apt 4B"
                      className="bg-input/50 mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>City</Label>
                      <Input
                        value={formData.city}
                        onChange={(e) => handleInputChange('city', e.target.value)}
                        placeholder="London"
                        className="bg-input/50 mt-1"
                        data-testid="kyc-city"
                      />
                    </div>
                    <div>
                      <Label>State/Province</Label>
                      <Input
                        value={formData.state_province}
                        onChange={(e) => handleInputChange('state_province', e.target.value)}
                        placeholder="Greater London"
                        className="bg-input/50 mt-1"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Postal Code</Label>
                      <Input
                        value={formData.postal_code}
                        onChange={(e) => handleInputChange('postal_code', e.target.value)}
                        placeholder="SW1A 1AA"
                        className="bg-input/50 mt-1"
                        data-testid="kyc-postal"
                      />
                    </div>
                    <div>
                      <Label>Country</Label>
                      <Input
                        value={formData.country}
                        onChange={(e) => handleInputChange('country', e.target.value)}
                        placeholder="United Kingdom"
                        className="bg-input/50 mt-1"
                        data-testid="kyc-country"
                      />
                    </div>
                  </div>
                </div>
                <div className="flex justify-between mt-6">
                  <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
                  <Button onClick={() => setStep(3)} className="font-heading uppercase">
                    Next: Documents
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 3: ID & Documents */}
            {step === 3 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <div className="flex items-center gap-2 mb-6">
                  <FileText className="w-5 h-5 text-primary" />
                  <h3 className="font-heading text-lg uppercase">Identity Documents</h3>
                </div>
                <div className="grid gap-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>ID Type</Label>
                      <Select
                        value={formData.id_type}
                        onValueChange={(value) => handleInputChange('id_type', value)}
                      >
                        <SelectTrigger className="bg-input/50 mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="passport">Passport</SelectItem>
                          <SelectItem value="drivers_license">Driver's License</SelectItem>
                          <SelectItem value="national_id">National ID Card</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>ID Number</Label>
                      <Input
                        value={formData.id_number}
                        onChange={(e) => handleInputChange('id_number', e.target.value)}
                        placeholder="AB123456"
                        className="bg-input/50 mt-1"
                        data-testid="kyc-id-number"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>ID Expiry Date</Label>
                    <Input
                      type="date"
                      value={formData.id_expiry_date}
                      onChange={(e) => handleInputChange('id_expiry_date', e.target.value)}
                      className="bg-input/50 mt-1"
                    />
                  </div>
                  
                  {/* Document Uploads */}
                  <div className="p-4 rounded-lg bg-secondary/30 border border-border mt-2">
                    <div className="flex items-center gap-2 mb-4">
                      <Upload className="w-4 h-4 text-primary" />
                      <span className="text-sm font-medium">Document Upload</span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-4">
                      Upload clear photos or scans of your documents. Files must be JPG, PNG, or PDF format (max 10MB each).
                    </p>
                    
                    <div className="grid gap-4">
                      <FileUploadBox 
                        label="ID Front *" 
                        documentType="id_front" 
                        inputRef={idFrontRef}
                      />
                      
                      {formData.id_type === 'drivers_license' && (
                        <FileUploadBox 
                          label="ID Back *" 
                          documentType="id_back" 
                          inputRef={idBackRef}
                        />
                      )}
                      
                      <FileUploadBox 
                        label="Selfie with ID *" 
                        documentType="selfie" 
                        inputRef={selfieRef}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label>Address Proof Type</Label>
                    <Select
                      value={formData.address_proof_type}
                      onValueChange={(value) => handleInputChange('address_proof_type', value)}
                    >
                      <SelectTrigger className="bg-input/50 mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="utility_bill">Utility Bill</SelectItem>
                        <SelectItem value="bank_statement">Bank Statement</SelectItem>
                        <SelectItem value="government_letter">Government Letter</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <FileUploadBox 
                    label="Address Proof Document (Optional)" 
                    documentType="address_proof" 
                    inputRef={addressProofRef}
                  />
                </div>
                <div className="flex justify-between mt-6">
                  <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
                  <Button onClick={() => setStep(4)} className="font-heading uppercase">
                    Next: Bank Details
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 4: Bank Details */}
            {step === 4 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <div className="flex items-center gap-2 mb-6">
                  <CreditCard className="w-5 h-5 text-primary" />
                  <h3 className="font-heading text-lg uppercase">Bank Details</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Required for receiving payouts. Please ensure details match your verified identity.
                </p>
                <div className="grid gap-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Bank Name</Label>
                      <Input
                        value={formData.bank_name}
                        onChange={(e) => handleInputChange('bank_name', e.target.value)}
                        placeholder="HSBC"
                        className="bg-input/50 mt-1"
                        data-testid="kyc-bank-name"
                      />
                    </div>
                    <div>
                      <Label>Account Holder Name</Label>
                      <Input
                        value={formData.bank_account_name}
                        onChange={(e) => handleInputChange('bank_account_name', e.target.value)}
                        placeholder="John Doe"
                        className="bg-input/50 mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Account Number</Label>
                    <Input
                      value={formData.bank_account_number}
                      onChange={(e) => handleInputChange('bank_account_number', e.target.value)}
                      placeholder="12345678"
                      className="bg-input/50 mt-1"
                      data-testid="kyc-account-number"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Routing Number (US) / Sort Code (UK)</Label>
                      <Input
                        value={formData.bank_routing_number}
                        onChange={(e) => handleInputChange('bank_routing_number', e.target.value)}
                        placeholder="12-34-56"
                        className="bg-input/50 mt-1"
                      />
                    </div>
                    <div>
                      <Label>SWIFT/BIC Code</Label>
                      <Input
                        value={formData.bank_swift_code}
                        onChange={(e) => handleInputChange('bank_swift_code', e.target.value)}
                        placeholder="HSBCGB2L"
                        className="bg-input/50 mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>IBAN (for international transfers)</Label>
                    <Input
                      value={formData.bank_iban}
                      onChange={(e) => handleInputChange('bank_iban', e.target.value)}
                      placeholder="GB82 WEST 1234 5698 7654 32"
                      className="bg-input/50 mt-1"
                    />
                  </div>
                </div>
                <div className="flex justify-between mt-6">
                  <Button variant="outline" onClick={() => setStep(3)}>Back</Button>
                  <Button 
                    onClick={handleSubmit} 
                    className="font-heading uppercase glow-green"
                    disabled={submitting}
                    data-testid="kyc-submit"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'SUBMIT VERIFICATION'}
                  </Button>
                </div>
              </motion.div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default KYCVerification;
